import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";

/**
 * Contact button + modal. Triggers a POST to /bio-api/contact. No email address
 * is rendered into the DOM — the recipient is resolved server-side from the
 * config. Honeypot field ("website") is hidden from users but submitted for
 * bot detection. Turnstile widget is only rendered when a site key is
 * provided via the `turnstileSiteKey` option (Phase 9 will wire it in).
 */
export function mountContactTrigger(
  cfg: ProfileConfig["contact"],
  options: { turnstileSiteKey?: string } = {},
): HTMLElement | null {
  if (!cfg.enabled) return null;

  const btn = el(
    "button",
    {
      class: "contact-trigger",
      type: "button",
      "data-tooltip": "Send a message",
      "aria-label": cfg.label,
    },
    [cfg.label],
  );

  btn.addEventListener("click", () => openModal(cfg, options));

  return btn;
}

function openModal(
  cfg: ProfileConfig["contact"],
  options: { turnstileSiteKey?: string },
) {
  const existing = document.querySelector<HTMLElement>(".contact-modal");
  if (existing) {
    existing.focus();
    return;
  }

  const modal = el("div", {
    class: "contact-modal",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": cfg.label,
    tabindex: "-1",
  });

  const backdrop = el("div", { class: "contact-modal__backdrop" });
  const panel = el("div", { class: "contact-modal__panel" });

  const title = el("h2", { class: "contact-modal__title" }, [cfg.label]);
  const closeBtn = el(
    "button",
    {
      class: "contact-modal__close",
      type: "button",
      "aria-label": "Close",
    },
    ["×"],
  );

  const form = el("form", { class: "contact-modal__form", novalidate: true }) as HTMLFormElement;
  form.append(
    field("name", "Name (optional)", "text", { maxLength: 80 }),
    field("email", "Reply-to email (optional)", "email", { maxLength: 160 }),
    field("subject", "Subject", "text", { maxLength: 120 }),
    fieldArea("message", "Message", { maxLength: 5000, required: true, rows: 5 }),
    // Honeypot — hidden from users, must stay empty.
    el("input", {
      type: "text",
      name: "website",
      class: "contact-modal__honeypot",
      tabindex: "-1",
      "aria-hidden": "true",
      autocomplete: "off",
    }),
  );

  // Turnstile placeholder. Phase 9 loads the actual script and replaces this.
  const captcha = el("div", { class: "contact-modal__captcha", "data-sitekey": options.turnstileSiteKey ?? "" });
  form.append(captcha);

  const status = el("p", { class: "contact-modal__status", "aria-live": "polite" });
  const actions = el("div", { class: "contact-modal__actions" }, [
    el("button", { type: "button", class: "contact-modal__btn contact-modal__btn--secondary" }, ["Cancel"]),
    el("button", { type: "submit", class: "contact-modal__btn contact-modal__btn--primary" }, ["Send"]),
  ]);
  actions.querySelectorAll("button")[0]!.addEventListener("click", close);

  form.append(status, actions);
  panel.append(closeBtn, title, form);
  modal.append(backdrop, panel);
  document.body.append(modal);

  requestAnimationFrame(() => {
    modal.classList.add("contact-modal--visible");
    (form.querySelector<HTMLElement>('[name="message"]') ?? panel).focus();
  });

  function close() {
    modal.classList.remove("contact-modal--visible");
    window.setTimeout(() => modal.remove(), 200);
    document.removeEventListener("keydown", onKey);
  }
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  backdrop.addEventListener("click", close);
  closeBtn.addEventListener("click", close);

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    status.textContent = "sending…";
    status.className = "contact-modal__status contact-modal__status--working";
    const fd = new FormData(form);
    const payload: Record<string, string> = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      subject: String(fd.get("subject") ?? ""),
      message: String(fd.get("message") ?? ""),
      website: String(fd.get("website") ?? ""), // honeypot
    };
    // Turnstile token — Phase 9 will populate via the widget; for now pass empty.
    const tsField = form.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]');
    if (tsField?.value) payload["turnstileToken"] = tsField.value;
    try {
      const r = await fetch("/bio-api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await r.json()) as { ok: boolean; error?: string; dev?: boolean };
      if (r.ok && data.ok) {
        status.textContent = data.dev ? "sent (dev mode: not actually delivered)" : "sent — thanks!";
        status.className = "contact-modal__status contact-modal__status--ok";
        form.reset();
        window.setTimeout(close, 1800);
      } else {
        status.textContent = errorLabel(data.error);
        status.className = "contact-modal__status contact-modal__status--err";
      }
    } catch {
      status.textContent = "network error";
      status.className = "contact-modal__status contact-modal__status--err";
    }
  });
}

function field(
  name: string,
  label: string,
  type: string,
  opts: { maxLength?: number; required?: boolean } = {},
): HTMLElement {
  const id = `cm-${name}`;
  const inputAttrs: Record<string, string | boolean | number> = {
    id,
    name,
    type,
    autocomplete: "off",
  };
  if (opts.maxLength) inputAttrs["maxlength"] = opts.maxLength;
  if (opts.required) inputAttrs["required"] = true;
  const input = el("input", inputAttrs);
  return el("label", { class: "contact-modal__field", for: id }, [
    el("span", { class: "contact-modal__label" }, [label]),
    input,
  ]);
}

function fieldArea(
  name: string,
  label: string,
  opts: { maxLength?: number; required?: boolean; rows?: number } = {},
): HTMLElement {
  const id = `cm-${name}`;
  const attrs: Record<string, string | boolean | number> = { id, name };
  if (opts.maxLength) attrs["maxlength"] = opts.maxLength;
  if (opts.required) attrs["required"] = true;
  if (opts.rows) attrs["rows"] = opts.rows;
  const ta = el("textarea", attrs);
  return el("label", { class: "contact-modal__field", for: id }, [
    el("span", { class: "contact-modal__label" }, [label]),
    ta,
  ]);
}

function errorLabel(code: string | undefined): string {
  switch (code) {
    case "rate limited":
      return "too many messages lately — try again later";
    case "captcha":
      return "captcha failed — try again";
    case "message required":
      return "please write a message";
    case "bad email":
      return "reply email looks invalid";
    default:
      return "couldn't send — try again";
  }
}
